import { useParams } from "react-router";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { isValidLang, type Lang } from "@/config/routes";
import { useLanguageSwitcher } from "@/hooks/use-language-switcher";

function FlagPL({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 640 480"
			className={className}
			aria-hidden="true"
		>
			<g fillRule="evenodd">
				<path fill="#fff" d="M640 480H0V0h640z" />
				<path fill="#dc143c" d="M640 480H0V240h640z" />
			</g>
		</svg>
	);
}

function FlagGB({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 640 480"
			className={className}
			aria-hidden="true"
		>
			<path fill="#012169" d="M0 0h640v480H0z" />
			<path
				fill="#FFF"
				d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0z"
			/>
			<path
				fill="#C8102E"
				d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z"
			/>
			<path fill="#FFF" d="M241 0v480h160V0zM0 160v160h640V160z" />
			<path fill="#C8102E" d="M0 193v96h640v-96zM273 0v480h96V0z" />
		</svg>
	);
}

const flagComponents: Record<Lang, typeof FlagPL> = {
	pl: FlagPL,
	en: FlagGB,
};

const languages = [
	{ code: "en" as Lang, label: "English" },
	{ code: "pl" as Lang, label: "Polski" },
] as const;

export function LanguageSwitcher({
	variant = "default",
}: {
	variant?: "default" | "dark";
}) {
	const { lang } = useParams<{ lang: string }>();
	const switchLanguage = useLanguageSwitcher();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";

	const triggerClass =
		variant === "dark"
			? "w-auto bg-muted text-foreground border-border [&_svg]:text-muted-foreground"
			: "w-full bg-sidebar-hover border-sidebar-border text-sidebar-foreground [&_svg]:text-sidebar-foreground/60";

	return (
		<Select
			value={currentLang}
			onValueChange={(v) => switchLanguage(v as Lang)}
		>
			<SelectTrigger className={triggerClass} aria-label="Language">
				<SelectValue />
			</SelectTrigger>
			<SelectContent className="min-w-[140px]">
				{languages.map((language) => {
					const Flag = flagComponents[language.code];
					return (
						<SelectItem key={language.code} value={language.code}>
							<span className="flex items-center gap-2">
								<Flag className="size-4 shrink-0 rounded-sm" />
								{language.label}
							</span>
						</SelectItem>
					);
				})}
			</SelectContent>
		</Select>
	);
}
